// ActivityOverview.tsx — graphe, carte et villes des onglets Chaînes, Vidéos, Assets
// et Enregistrements du Dashboard
//
// Ajouté le 2026-10-20 : ces quatre onglets reprennent la présentation de l'onglet
// Trafic (TrafficSection.tsx). Le graphe compte les créations par heure (par
// 10 minutes sur une journée, si on le veut) ou par jour, empilées par série (origine
// d'une chaîne, état d'une vidéo, type d'un asset, nature d'une prise). Survoler une
// barre ne montre plus que cette tranche sur la carte, dans les villes et dans la
// liste de l'onglet ; un clic la fige, un second clic sur la même barre la libère.
//
// La position d'un élément vient d'admin-api (champ `geo`) : celle de la dernière
// connexion réussie du créateur avant la création (CreatorWhereabouts), ou, pour une
// chaîne posée sur le globe, sa position de création.
//
// <ActivityTab> garde la tranche et rend à l'onglet, par `children`, les éléments de la
// tranche, le bloc graphe + carte + villes à placer sous ses chiffres, et la tranche.
//
// `extra` (onglet Chaînes, 2026-10-20) : une couche de points de plus sur la carte —
// les smartphones connectés aux régies — filtrée par la même tranche, avec son choix
// dans le sélecteur de la carte.

import React, { useMemo, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';

import OpenStreetMapV2 from '../../widget/dataDisplay/maps/OpenStreetMapV2';
import ExtendablePanel from '../../widget/windows/ExtendablePanel';
import { Coordinate } from '../../../dataTypes/geolocationManagment/types';
import { Segment, plural, customRange } from './dashboardShared';

export interface Geo { lat: number; lon: number; city: string | null; country: string | null }

export type Period = 'today' | 'yesterday' | 'week' | 'month' | 'custom';
type Step = 'tenMinutes' | 'hour';

/** Une série du graphe et de la carte : ses éléments s'empilent dans cette couleur. */
export interface Series { id: string; label: string; color: string }

/** Une couche de points ajoutée à la carte, hors graphe. */
// `key` : un point qu'on peut désigner d'un clic sur la carte (rendu à l'onglet par
// `children`, qui surligne la ligne correspondante). `color` d'un point : sa couleur s'il diffère de la couche (régies parmi les smartphones),
// `legend` les entrées correspondantes. Un point sans `city` ne compte pas dans Villes,
// où la couche s'appelle `cityLabel` (à défaut `label`).
export interface ExtraLayer { id: string; label: string; color: string; cityLabel?: string;
                              legend?: { label: string; color: string }[];
                              points: { at: number; lat: number; lon: number; city: string | null; place: string; info: string; color?: string; key?: string }[] }

/** Ce qu'un onglet dit de ses éléments pour les placer dans le temps et sur la carte. */
export interface ActivitySpec<T> {
  noun    : { one: string; many: string };   // « chaîne créée » / « chaînes créées »
  title   : string;                          // « Chaînes créées » (titre du graphe)
  series  : Series[];
  seriesOf: (x: T) => string;
  dateOf  : (x: T) => string | null;
  geoOf   : (x: T) => Geo | null | undefined;
  nameOf  : (x: T) => string;
}

export const range = (p: Period, from: Date, to: Date): [Date, Date] => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  if (p === 'yesterday') {
    // La veille en entier, de 00:00:00 à 23:59:59 : un jour unique, jamais « en direct ».
    const y = now.getDate() - 1;
    return [ new Date(now.getFullYear(), now.getMonth(), y, 0, 0, 0), new Date(now.getFullYear(), now.getMonth(), y, 23, 59, 59) ];
  }
  if (p === 'week')  return [ new Date(start.getTime() - 6 * 86_400_000), end ];
  if (p === 'month') return [ new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0), end ];
  if (p === 'custom') return customRange(from, to);
  return [ start, end ];
};

const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
/** Jours de calendrier touchés par la plage (« Personnaliser » à la minute : 22:00 → 02:00 en touche deux). */
const calendarDays = (start: Date, end: Date) => Math.round((midnight(end) - midnight(start)) / 86_400_000) + 1;

export const periodText = (p: Period) =>
  p === 'today' ? "aujourd'hui" : p === 'yesterday' ? 'hier' : p === 'week' ? 'sur 7 jours' : p === 'month' ? 'ce mois-ci' : 'sur la période';

/** Tout le texte d'un élément (y compris sa ville), pour la recherche de la barre d'outils. */
export const textOf = (x: unknown): string =>
  x == null ? '' : typeof x === 'object' ? Object.values(x as object).map(textOf).join(' ') : String(x);

/** Rayon du cercle où se rangent les points d'un même endroit sur la carte (≈ 1 km). */
const SPREAD_DEG = 0.01;

/** Un maximum d'axe « rond » (10, 20, 50, 100…) et son pas, pour 4 à 5 graduations. */
const niceScale = (max: number): [number, number] => {
  const raw  = Math.max(4, max) / 4;
  const pow  = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map(m => m * pow).find(s => s >= raw) ?? 10 * pow;
  return [ Math.ceil(Math.max(1, max) / step) * step, step ];
};

// ── Graphe ───────────────────────────────────────────────────────────────────

/** Une barre : sa tranche [from, to[ en millisecondes, et ses éléments par série. */
type Bucket = { label: string; counts: number[]; current: boolean; from: number; to: number };

const Chart: React.FC<{ buckets: Bucket[]; series: Series[]; labelEvery: number; activeFrom: number | null; pinnedFrom: number | null;
                        noun: { one: string; many: string }; onHover: (i: number | null) => void; onPick: (i: number) => void }> =
  ({ buckets, series, labelEvery, activeFrom, pinnedFrom, noun, onHover, onPick }) => {
  const W = 960, H = 240, L = 34, R = 8, T = 16, B = 26;   // pleine largeur, comme le graphique de Trafic
  const total = (b: Bucket) => b.counts.reduce((a, n) => a + n, 0);
  const [ max, step ] = niceScale(Math.max(...buckets.map(total), 0));
  const cw = (W - L - R) / Math.max(1, buckets.length);
  const y  = (v: number) => T + (H - T - B) * (1 - v / max);
  const gap = Math.min(6, cw * 0.25);
  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);
  const detail = (b: Bucket) => series.map((s, k) => b.counts[k] ? `${b.counts[k]} ${s.label.toLowerCase()}` : '').filter(Boolean).join(', ');

  return (
    <svg className={tabs.chart} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={noun.many} onMouseLeave={() => onHover(null)}>
      {ticks.map(v => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#1C3557" strokeWidth="1" />
          <text x={L - 6} y={y(v) + 4} textAnchor="end">{v}</text>
        </g>
      ))}
      {buckets.map((b, i) => {
        const x = L + i * cw + gap / 2, w = Math.max(1, cw - gap);
        const active = activeFrom === b.from, pinned = pinnedFrom === b.from;
        const dim = activeFrom !== null && !active;
        const n = total(b);
        let base = 0;
        return (
          <g key={b.from} opacity={dim ? 0.35 : 1}>
            {(active || pinned) && <rect x={L + i * cw} y={T} width={cw} height={H - T - B} fill={pinned ? 'rgba(245,158,11,0.10)' : 'rgba(93,151,241,0.10)'} />}
            {n === 0
              ? <rect x={x} y={y(0) - 2} width={w} height={2} fill="#1C3557" />
              : series.map((s, k) => {
                  const c = b.counts[k];
                  if (!c) return null;
                  const r = <rect key={s.id} x={x} y={y(base + c)} width={w} height={y(base) - y(base + c)} rx={Math.min(2, w / 2)} fill={s.color} />;
                  base += c;
                  return r;
                })}
            {pinned && <rect x={L + i * cw + 0.5} y={T + 0.5} width={Math.max(1, cw - 1)} height={H - T - B - 1} fill="none" stroke="#F59E0B" strokeWidth="1.5" />}
            {(active || (b.current && activeFrom === null)) && n > 0 &&
              <text className={tabs.chartValue} x={x + w / 2} y={y(n) - 5} textAnchor="middle">{n}</text>}
            {i % labelEvery === 0 && <text x={x + w / 2} y={H - 8} textAnchor="middle">{b.label}</text>}
            {/* Zone de survol et de clic : toute la hauteur de la tranche, comme dans Trafic. */}
            <rect x={L + i * cw} y={T} width={cw} height={H - T - B} fill="transparent" className={tabs.barHit}
                  role="button" tabIndex={0}
                  aria-label={`${b.label} : ${plural(n, noun.one, noun.many)}${n ? ` (${detail(b)})` : ''}${pinned ? ' (figée)' : ''}`}
                  onMouseEnter={() => onHover(i)} onFocus={() => onHover(i)} onBlur={() => onHover(null)}
                  onClick={e => { onPick(i); if (e.detail > 0) (e.currentTarget as SVGRectElement).blur(); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(i); } }}>
              <title>{`${b.label} : ${plural(n, noun.one, noun.many)}${n ? ` (${detail(b)})` : ''}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
};

// ── Composant ────────────────────────────────────────────────────────────────

/** Le point désigné sur la carte (sa `key`), de quoi en changer, et les éléments de
 *  l'onglet qu'il représente (vide pour un point de la couche `extra`). */
export interface Picked<T = unknown> { key: string | null; set: (key: string | null) => void; items: T[] }

interface Props<T> {
  items    : T[];
  spec     : ActivitySpec<T>;
  period   : Period;
  from     : Date;
  to       : Date;
  readAt   : number;            // change à chaque relevé : la barre « maintenant » avance
  extra?   : ExtraLayer;
  /** Les points des éléments de l'onglet se désignent aussi (l'onglet surligne `picked.items`). */
  pickItems? : boolean;
  children : (shown: T[], overview: React.ReactNode, slice: { from: number; to: number } | null,
              picked: Picked<T>) => React.ReactNode;
}

export function ActivityTab<T>({ items, spec, period, from, to, readAt, extra, pickItems, children }: Props<T>) {
  const [ step   , setStep    ] = useState<Step>('hour');
  const [ mapShow, setMapShow ] = useState<string>('all');
  const [ hovered, setHovered ] = useState<number | null>(null);
  const [ pinned , setPinned  ] = useState<{ from: number; to: number } | null>(null);

  const [ start, end ] = range(period, from, to);
  const singleDay  = calendarDays(start, end) === 1;
  const dayStart   = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const todayStart = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const live       = singleDay && dayStart === todayStart;

  // Changer de période ou de pas redessine d'autres barres : la tranche figée n'a plus de sens.
  const shape = `${period}|${start.getTime()}|${end.getTime()}|${step}`;
  const [ lastShape, setLastShape ] = useState(shape);
  if (shape !== lastShape) { setLastShape(shape); setPinned(null); setHovered(null); }

  const timed = useMemo(() => items.map(x => {
    const d = spec.dateOf(x);
    return { x, t: d ? new Date(d).getTime() : NaN, k: spec.series.findIndex(s => s.id === spec.seriesOf(x)) };
  }), [items, spec]);

  const buckets = useMemo((): Bucket[] => {
    const empty = () => spec.series.map(() => 0);
    let out: Bucket[];
    if (singleDay) {
      const size = step === 'tenMinutes' ? 600_000 : 3_600_000;
      const n = live && step === 'tenMinutes' ? Math.floor((Date.now() - dayStart) / size) + 1 : 86_400_000 / size;
      const nowI = Math.floor((Date.now() - dayStart) / size);
      out = Array.from({ length: n }, (_, i) => {
        const t = new Date(dayStart + i * size);
        return { label: step === 'hour' ? `${String(t.getHours()).padStart(2, '0')} h` : t.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                 counts: empty(), current: live && i === nowI, from: dayStart + i * size, to: dayStart + (i + 1) * size };
      });
    } else {
      const days = Math.min(62, calendarDays(start, end));
      out = Array.from({ length: days }, (_, i) => {
        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        return { label: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric' }), counts: empty(),
                 current: d.toDateString() === new Date().toDateString(),
                 from: d.getTime(), to: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() };
      });
    }
    timed.forEach(({ t, k }) => {
      if (!isFinite(t)) return;
      const b = out.find(x => t >= x.from && t < x.to);
      if (b) b.counts[k < 0 ? spec.series.length - 1 : k]++;
    });
    return out;
  }, [timed, singleDay, live, step, dayStart, readAt, spec, start.getTime(), end.getTime()]);   // eslint-disable-line react-hooks/exhaustive-deps

  const labelEvery = singleDay ? (step === 'tenMinutes' ? (buckets.length > 72 ? 18 : 9) : 3) : Math.max(1, Math.ceil(buckets.length / 8));
  const chartTitle = `${spec.title} ${!singleDay ? 'par jour' : step === 'tenMinutes' ? 'par 10 minutes' : 'par heure'}`;

  // ── La tranche choisie : figée par un clic, sinon survolée ──
  const hoveredBucket = hovered !== null ? buckets[hovered] ?? null : null;
  const slice = pinned ?? (hoveredBucket ? { from: hoveredBucket.from, to: hoveredBucket.to } : null);
  const sliceItems = useMemo(() => slice === null ? items : timed.filter(e => e.t >= slice.from && e.t < slice.to).map(e => e.x),
                                   [items, timed, slice?.from, slice?.to]);   // eslint-disable-line react-hooks/exhaustive-deps
  const sliceText = slice === null ? '' : (() => {
    const a = new Date(slice.from), b = new Date(slice.to);
    const t = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return slice.to - slice.from >= 86_400_000 ? `le ${a.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}` : `de ${t(a)} à ${t(b)}`;
  })();
  const pick = (i: number) => {
    const b = buckets[i];
    if (!b) return;
    setPinned(p => p && p.from === b.from ? null : { from: b.from, to: b.to });
  };

  // ── Carte : un point par lieu et par série ──
  const colorOf = (id: string) => spec.series.find(s => s.id === id)?.color ?? '#64748B';
  const mapPoints = useMemo(() => {
    const m = new Map<string, { lat: number; lng: number; series: string; names: string[]; place: string; items: T[] }>();
    let unlocated = 0;
    for (const x of mapShow === extra?.id ? [] : sliceItems) {
      const s = spec.seriesOf(x);
      if (mapShow !== 'all' && s !== mapShow) continue;
      const g = spec.geoOf(x);
      const lat = Number(g?.lat), lng = Number(g?.lon);
      if (!g || !isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) { unlocated++; continue; }
      const k = `${lat.toFixed(2)}|${lng.toFixed(2)}|${s}`;
      const e = m.get(k) ?? { lat, lng, series: s, names: [], place: [g.city, g.country].filter(Boolean).join(', ') || 'Lieu inconnu', items: [] };
      e.names.push(spec.nameOf(x));
      e.items.push(x);
      m.set(k, e);
    }
    // Plusieurs points au même endroit : disposés en petit cercle autour de lui (spread, plus bas).
    const seen = new Map<string, number>();
    const spots: string[] = [];   // en regard de `coordinates` : l'endroit d'origine du point
    const keys: (string | null)[] = [];   // en regard de `coordinates` : la clé du point désignable
    const itemsOf = new Map<string, T[]>();
    const coordinates: Coordinate[] = Array.from(m.entries()).map(([k, e]) => {
      keys.push(pickItems ? `items:${k}` : null);
      itemsOf.set(`items:${k}`, e.items);
      const spot = `${e.lat.toFixed(2)}|${e.lng.toFixed(2)}`;
      seen.set(spot, (seen.get(spot) ?? 0) + 1);
      spots.push(spot);
      const label = spec.series.find(s => s.id === e.series)?.label ?? '';
      return { lat: e.lat, lng: e.lng, info1: e.place,
               info2: `${plural(e.names.length, spec.noun.one, spec.noun.many)} · ${label} · ${e.names.slice(0, 2).join(', ')}${e.names.length > 2 ? '…' : ''}`,
               color: colorOf(e.series) };
    });
    // La couche en plus (smartphones), même tranche, même cercle pour rester visible.
    if (extra && (mapShow === 'all' || mapShow === extra.id)) {
      extra.points.filter(p => slice === null || (p.at >= slice.from && p.at < slice.to)).forEach(p => {
        const spot = `${p.lat.toFixed(2)}|${p.lon.toFixed(2)}`;
        seen.set(spot, (seen.get(spot) ?? 0) + 1);
        spots.push(spot);
        coordinates.push({ lat: p.lat, lng: p.lon, info1: p.place, info2: p.info, color: p.color ?? extra.color });
        keys.push(p.key ?? null);
      });
    }
    // Avant le 2026-10-24, chaque point de trop était poussé de 0,04° vers l'est : quinze
    // connexions depuis la même IP finissaient à 50 km de leur ville. Désormais ils se
    // rangent sur un cercle d'environ 1 km autour d'elle, quel que soit leur nombre.
    const rank = new Map<string, number>();
    spots.forEach((spot, i) => {
      const total = seen.get(spot) ?? 1;
      if (total < 2) return;
      const n = rank.get(spot) ?? 0;
      rank.set(spot, n + 1);
      const angle = 2 * Math.PI * n / total;
      const c = coordinates[i];
      coordinates[i] = { ...c, lat: c.lat + SPREAD_DEG * Math.sin(angle),
                         lng: c.lng + SPREAD_DEG * Math.cos(angle) / Math.cos(c.lat * Math.PI / 180) };
    });
    return { coordinates, keys, itemsOf, unlocated, places: seen.size };
  }, [sliceItems, mapShow, spec, extra, pickItems, slice?.from, slice?.to]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Point désigné : un clic le choisit, un second clic (ou un clic dans le vide) le relâche.
  const [ pickedKey, setPickedKey ] = useState<string | null>(null);
  const picked = useMemo<Picked<T>>(() => ({ key: pickedKey, set: setPickedKey, items: (pickedKey && mapPoints.itemsOf.get(pickedKey)) || [] }),
                               [pickedKey, mapPoints]);
  const coordinates = useMemo(() => mapPoints.coordinates.map((c, i) =>
      pickedKey !== null && mapPoints.keys[i] === pickedKey ? { ...c, selected: true } : c), [mapPoints, pickedKey]);
  const pointClick = (i: number | null) => {
    const k = i === null ? null : mapPoints.keys[i];
    setPickedKey(p => k !== null && p !== k ? k : null);
  };

  // ── Villes ──
  // Les éléments de l'onglet, puis la couche en plus (smartphones) dans sa couleur, même tranche.
  const extraIn = useMemo(() => (extra?.points ?? []).filter(p => slice === null || (p.at >= slice.from && p.at < slice.to)),
                          [extra, slice?.from, slice?.to]);   // eslint-disable-line react-hooks/exhaustive-deps
  const cities = useMemo(() => {
    const m = new Map<string, { n: number; x: number }>();
    sliceItems.forEach(x => { const c = spec.geoOf(x)?.city; if (c) { const e = m.get(c) ?? { n: 0, x: 0 }; e.n++; m.set(c, e); } });
    extraIn.forEach(p => { if (p.city) { const e = m.get(p.city) ?? { n: 0, x: 0 }; e.x++; m.set(p.city, e); } });
    return Array.from(m.entries()).sort((a, b) => (b[1].n + b[1].x) - (a[1].n + a[1].x)).slice(0, 7);
  }, [sliceItems, spec, extraIn]);
  const topCity = Math.max(1, ...cities.map(([, e]) => e.n + e.x));

  const noun = spec.noun;
  const overview = (
    <>
    {/* ── Graphe ── */}
    <div className={tabs.box}>
      <div className={tabs.boxHead}>
        <h3>{chartTitle}</h3>
        <span className={tabs.legend}>{spec.series.map(s => <span key={s.id}><i style={{ background: s.color }}/>{s.label}</span>)}</span>
        <span className={styles.spacer}/>
        {singleDay &&
          <Segment label="Pas du graphique" value={step} onChange={setStep}
                   choices={[ ['tenMinutes', '10 minutes'], ['hour', 'Heure'] ] as const} />}
      </div>
      <Chart buckets={buckets} series={spec.series} labelEvery={labelEvery} noun={noun}
             activeFrom={slice?.from ?? null} pinnedFrom={pinned?.from ?? null}
             onHover={setHovered} onPick={pick} />
      <div className={tabs.sliceBar} aria-live="polite">
        {pinned
          ? <><span className={tabs.slicePinned}>FIGÉ</span>
              <span>Carte, villes et liste : {noun.many} {sliceText} ({plural(sliceItems.length, noun.one, noun.many)}).</span>
              <button type="button" className={styles.secondary} onClick={() => setPinned(null)}>Revenir à toute la période</button></>
          : slice
            ? <span>{noun.many.charAt(0).toUpperCase() + noun.many.slice(1)} {sliceText} : {plural(sliceItems.length, noun.one, noun.many)}. Cliquez la barre pour figer.</span>
            : <span className={styles.muted}>Survolez une barre pour voir ses {noun.many} sur la carte, dans les villes et la liste ; cliquez pour figer.</span>}
      </div>
    </div>
      {/* ── Carte et villes ── */}
      <div className={tabs.twoCol}>
        <div className={tabs.box}>
          <div className={tabs.boxHead}>
            <h3>Carte</h3>
            {extra && <span className={tabs.legend}>{(extra.legend ?? [extra]).map(l => <span key={l.label}><i style={{ background: l.color, borderRadius: '50%' }}/>{l.label}</span>)}</span>}
            {slice && <span className={styles.muted} style={{ fontSize: '0.85rem' }}>{sliceText}</span>}
            <span className={styles.spacer}/>
            <Segment label="Éléments montrés" value={mapShow} onChange={setMapShow}
                     choices={[ ['all', 'Tous'] as const, ...spec.series.map(s => [s.id, s.label] as const),
                                ...(extra ? [ [extra.id, extra.label] as const ] : []) ]} />
          </div>
          <div className={tabs.map}>
            <ExtendablePanel logoUrl="/images/logo.png" logoPosition="bottomLeft">
              <OpenStreetMapV2
                coordinates  = {coordinates}
                onPointClick = {extra || pickItems ? pointClick : undefined}
                clickToZoom
                initialZoom  = {2}
                renderMode   = "dark"
                mapColors    = {{
                  water     : '#1b2947',
                  land      : '#263d6e',
                  landcover : '#263d6e',
                  road      : '#2a2a4a',
                  building  : '#222244',
                  labelText : '#c8d6e5',
                  labelHalo : '#0d1b2a',
                }}
                cityLabelsOnly
                // Pas de recadrage pendant qu'on parcourt les barres : la vue sauterait à chaque survol.
                autoCenter   = {slice === null}
                fitPoints    = {true}
              />
            </ExtendablePanel>
            <span className={tabs.mapNote}>
              {plural(mapPoints.places, 'lieu', 'lieux')}{mapPoints.unlocated > 0 ? ` · ${mapPoints.unlocated} sans position` : ''}
              {(extra || pickItems) && (pickedKey ? ' · point sélectionné : recliquez-le pour le relâcher' : ' · cliquez un point pour le retrouver dans la liste')}
            </span>
          </div>
        </div>
        {/* Les villes prennent la hauteur de la carte. */}
        <div className={tabs.sideStack}>
          <div className={`${tabs.box} ${tabs.citiesBox}`}>
            <div className={tabs.boxHead}>
              <h3>Villes</h3><span className={styles.muted} style={{ fontSize: '0.85rem' }}>{slice ? sliceText : "d'après la connexion du créateur"}</span>
              {extra && extraIn.length > 0 &&
                <span className={tabs.legend}><span><i style={{ background: '#2C7DE1' }}/>{noun.many.charAt(0).toUpperCase() + noun.many.slice(1)}</span><span><i style={{ background: extra.color }}/>{extra.cityLabel ?? extra.label}</span></span>}
            </div>
            {cities.length === 0
              ? <div className={styles.muted}>Aucune ville connue {periodText(period)}.</div>
              : <div className={tabs.bars}>
                  {cities.map(([city, e]) => (
                    <div key={city} className={tabs.barRow}
                         title={extra && e.x ? `${city} : ${plural(e.n, noun.one, noun.many)}, ${plural(e.x, (extra.cityLabel ?? extra.label).toLowerCase().replace(/s$/, ''), (extra.cityLabel ?? extra.label).toLowerCase())}` : undefined}>
                      <span title={city}>{city}</span>
                      <span className={tabs.track} style={{ display: 'flex' }}>
                        {e.n > 0 && <i style={{ width: `${e.n / topCity * 100}%`, borderRadius: 0 }}/>}
                        {e.x > 0 && <i style={{ width: `${e.x / topCity * 100}%`, borderRadius: 0, background: extra!.color }}/>}
                      </span>
                      <span>{e.n + e.x}</span>
                    </div>
                  ))}
                </div>}
          </div>
        </div>
      </div>

      {slice && <h3 className={tabs.sectionTitle}>Détail {sliceText} <span className={styles.muted}>· {plural(sliceItems.length, noun.one, noun.many)}</span></h3>}
    </>
  );

  return <>{children(sliceItems, overview, slice, picked)}</>;
}
